import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa6';
import { IoIosArrowDown } from 'react-icons/io';

import cn from '@/lib/clsxm';
import { APIErrorResponseGenerator } from '@/lib/helper';

import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import { showToast } from '@/components/@base/toast/show-toast';
import { Text } from '@/components/@base/typography';
import Rating from '@/components/app/product/components/side-bar/rate-action/rating';
import Props from '@/components/app/product/components/side-bar/rate-action/type';
import { useGetAddOnsInfo, usePostRate } from '@/components/app/product/services/hooks';

import { pageLevelLocalization } from '@/constant/localization';

const { product: productLocalization } = pageLevelLocalization;

export default function RateAction({ productId, slug, className }: Props) {
  const { data: addonsData } = useGetAddOnsInfo({
    slug,
  });
  const [open, setOpen] = useState<boolean>(false);
  const [selectedRate, setSelectedRate] = useState<number>(0);
  const { mutate: postRate, isPending } = usePostRate();
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [comment, setComment] = useState('');
  const [textAreaHeight, setTextAreaHeight] = useState('auto');

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);

    e.currentTarget.style.height = 'auto';
    const newHeight = Math.min(e.currentTarget.scrollHeight, 158);
    e.currentTarget.style.height = newHeight + 'px';

    setTextAreaHeight(newHeight + 'px');
  };
  //if the user is not a buyer of this product don't show the rating box
  if (!addonsData || !addonsData.isProductBuyer || addonsData.rated || !isVisible) {
    return null;
  }

  const handleSaveRate = () => {
    if (selectedRate === 0) {
      showToast({ message: productLocalization.pleaseSelectARate, type: 'error' });
      if (!comment) {
        setShowError(true);
        setIsOpen(true);
      }
      return;
    }
    if (!comment) {
      setShowError(true);
      setIsOpen(true);
      showToast({ message: productLocalization.addYourRateAndComment, type: 'error' });
      return;
    }
    // rate post request
    postRate(
      {
        product_id: productId,
        point: selectedRate,
        description: comment,
      },
      {
        onSuccess: () => {
          showToast({ message: productLocalization.rateSavedSuccessfully, type: 'success' });
          setIsVisible(false);
        },
        onError: (error) => {
          if (error instanceof AxiosError) {
            showToast(APIErrorResponseGenerator(error));
          }
        },
      },
    );
  };

  const handleSelectRate = (val: number) => {
    setSelectedRate(val);
    setIsOpen(true);
  };
  return (
    <Container center className={cn('flex-col rounded-md bg-[#F5FBED] p-3', className)}>
      <Container
        center
        className={cn(
          'max-h-0 w-full flex-col overflow-hidden border-b-[0.5px] border-b-[#DDFBC0] opacity-0 transition-all duration-300',
          open && 'mb-3 max-h-60 pb-3 opacity-100',
        )}
      >
        <Container className='flex w-full justify-between'>
          <Container></Container>
          <Rating onSelect={handleSelectRate} totalStars={5} size='text-3xl mr-4 md:text-xl' />
          <Button
            onClick={() => {
              setIsOpen(!isOpen);
            }}
            className='h-5 w-5 rounded-[4px] border border-[#EEF0F4] bg-[#F5FBED]! p-1.5 hover:bg-gray-100'
          >
            <IoIosArrowDown
              className={cn(
                'text-[#878F9B] transition duration-300',
                isOpen ? 'rotate-180' : 'rotate-0',
              )}
            />
          </Button>
        </Container>
        {isOpen && (
          <Container className='mt-4 w-full'>
            <div
              className={cn('overflow-hidden transition-all duration-300 ease-in-out')}
              onClick={() => {
                setIsOpen(true);
              }}
            >
              <textarea
                required
                rows={4}
                placeholder={productLocalization.addYourRateAndComment}
                className={
                  'no-scrollbar! w-full resize-none rounded-md border p-2 text-[12px] text-[#424244] focus:outline-none ' +
                  (showError
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-[#E6E9EF] focus:border-[#FFB616]')
                }
                value={comment}
                onChange={handleChange}
                style={{ height: textAreaHeight, maxHeight: '158px' }}
              />
            </div>
          </Container>
        )}
      </Container>

      <Container center className='w-full justify-between'>
        <Text className='text-[15px] text-[#81CF33]'>
          {productLocalization.giveRateToProduct}
        </Text>
        {open ? (
          <Button
            className='h-10 rounded-[5px] bg-[#81CF33] text-[15px] font-bold hover:bg-[#68aa27]'
            onClick={handleSaveRate}
            isLoading={isPending}
          >
            {productLocalization.saveRate}
          </Button>
        ) : (
          <Button
            className='h-10 w-10 rounded-[5px] bg-[#81CF33] hover:bg-[#68aa27]'
            onClick={() => setOpen(true)}
          >
            <FaArrowLeft fill='#FFFFFF' size={15} />
          </Button>
        )}
      </Container>
    </Container>
  );
}
